// ============================================================================
// LA TUILE D'ARTICLE RAPIDE — ses mesures, ses versions et la grille d'un niveau, UNE fois pour la caisse et pour le
// studio du back-office.
//
// UTILITÉ : le studio dessine un aperçu « de ce que la caisse affichera » ; la caisse dessine la vraie grille. Chacun a
// son moteur de rendu (DOM, React Native), mais les NOMBRES et les RÈGLES vivent ici : la version d'une tuile, sa
// hauteur, la place de chaque tuile et la hauteur de chaque rangée. Écrites deux fois, elles divergeraient au premier
// réglage, et l'aperçu deviendrait un autre écran que celui que le caissier touche (Forme A).
// ⚠️ PUR : des nombres et des fonctions, aucun composant — lu par le DOM (studio) et par React Native (caisse).
// ============================================================================
import type { IconName } from './icons';

/**
 * LES PICTOS QU'UNE TUILE PEUT PORTER — la liste FIXE que l'inspecteur du studio propose, dans l'ordre de la maquette
 * (commerce, boissons et restauration, puis mode et bijoux).
 * ⚠️ UNE LISTE ET NON LE REGISTRE ENTIER : les icônes d'interface (« sortie », « engrenage », « flèche ») ne disent rien
 *    d'un rayon, et les proposer faisait chercher un picto de commerce parmi une centaine d'outils (décision A12).
 * ⚠️ SANS LA POIGNÉE (`drag`), que la maquette range avec les pictos : posée sur une tuile, elle se confondrait avec
 *    la poignée qui la déplace.
 * ⚠️ UNE TUILE QUI PORTE UN AUTRE NOM DU REGISTRE LE GARDE : la liste borne ce qu'on CHOISIT, pas ce qu'on dessine.
 */
export const QUICK_TILE_ICONS = [
  'grid', 'tag', 'box', 'star', 'bag', 'cart', 'sparkles', 'layers', 'cup', 'coffee', 'iceCream', 'scale', 'image',
  'tshirt', 'turtleneck', 'pants', 'dress', 'shoe', 'hat', 'cap', 'beanie', 'scarf', 'belt', 'glasses', 'watch', 'ring',
  'necklace', 'earrings',
] as const satisfies readonly IconName[];

/** La colonne de référence, la gouttière et le format du bandeau photo, en pixels logiques de la tablette. */
export const QUICK_TILE = {
  /** Largeur de référence — et largeur minimale d'une colonne dès qu'un niveau porte une photo ou un grand picto. */
  width: 150,
  gap: 10,
  /** Le format du bandeau photo d'une vignette : 16:10 — sa hauteur suit la LARGEUR de la colonne. */
  band: { width: 16, height: 10 },
} as const;

/**
 * LES TUILES CARRÉES — le côté d'une tuile 1×1 « texte seul », « petit picto » et « grand picto », par classe de taille :
 * le minimum, la CIBLE et le maximum d'une colonne quand la caisse choisit ses colonnes (maquette du studio, `QS`).
 * ⚠️ LES PETITS PICTOS NETTEMENT PLUS PETITS QUE LES GRANDS (≈ 112 contre 150), y compris dans un niveau tout en petits
 *    pictos : la grille VISE la cible — elle ne gonfle plus ses cases pour remplir la largeur (`quickGridColumns`).
 * - `imposed` : la colonne la plus étroite quand la disposition IMPOSE un nombre de colonnes (texte seul, petit picto) ;
 *   les autres classes gardent la largeur de référence.
 */
export const QUICK_TILE_SQUARE = {
  text: { min: 96, target: 104, max: 112 },
  icon: { min: 106, target: 112, max: 118 },
  large: { min: 144, target: 150, max: 156 },
  imposed: { text: 104, icon: 106 },
} as const;

/**
 * LE CORPS D'UNE TUILE — ses marges, ses lignes et ses visuels, pour toutes ses versions.
 * ⚠️ TOUT CE QUI FAIT UNE HAUTEUR EST ICI : la hauteur d'une tuile se CALCULE à partir de ces nombres
 *    (`quickTileHeight`), elle ne se mesure jamais — mesurée, elle dépendrait de la police, et la tablette (police du
 *    système) et le studio (Inter Tight) ne tomberaient plus au même pixel.
 */
export const QUICK_TILE_BODY = {
  radius: 14,
  border: 1,
  /** Du filet au contenu : 9 + le filet = 10. */
  pad: 9,
  /** Le nom d'un dossier ou d'une source : 14 px gras. */
  name: { size: 14, line: 17 },
  /** Le nom d'un article : 13 px. */
  articleName: { size: 13, line: 16 },
  /**
   * Le nom sous un bandeau ou dans une rangée (vignette, photo réduite, photo entière) : UNE ligne, coupée par « … » —
   * c'est elle qui entre dans la hauteur de ces versions. Un carré, une photo à gauche et un texte seul en ont plus
   * (`quickNameLines`) : leur hauteur ne dépend pas de leur nom.
   */
  nameLines: 1,
  /**
   * ⚠️ LE NOM D'UN CARRÉ À PICTO PASSE À DEUX LIGNES quand le carré a la hauteur de les poser (décision de l'utilisateur
   *    du 2026-10-15, qui revient sur la ligne unique du 2026-10-06 : « ce que la maquette propose, deux lignes et la
   *    deuxième avec les … ») — la hauteur INTÉRIEURE à partir de laquelle (maquette `nL`) : 94 sous le picto d'un dossier
   *    (le nom y est suivi de sa pastille), 90 sous celui d'un article (suivi de son prix). Un grand picto et une photo à
   *    gauche en ont toujours deux, un texte seul trois (`textLines`).
   */
  twoLinesFrom: { folder: 94, article: 90 },
  textLines: 3,
  /** ⚠️ LA CIBLE TACTILE D'UN DOIGT : aucune tuile n'est moins haute, quelle que soit sa version (48 px, la caisse). */
  touch: 48,
  /**
   * ⚠️ LA PLACE QU'UN NOM GARDE AU MOINS — environ huit lettres en gras de 14 px. En dessous, le visuel posé À CÔTÉ du
   *    nom s'efface (`quickFitVariant`) : sur un petit écran, « une icône et deux lettres » ne se lit pas. Seule la photo
   *    « à gauche » se pose à côté du nom — le picto d'une tuile carrée est AU-DESSUS, le nom y a toute la largeur.
   */
  nameMin: 64,
  /**
   * La pastille d'un dossier ou d'une source : ce qu'on y trouve EN TOUTES LETTRES (« 12 articles », « vide »), resserrée
   * (`narrow`) quand le libellé ne tient pas (`quickPillForm`). Une tuile « texte seul » l'élargit un peu (`compactPadX`).
   */
  pill: { height: 22, size: 12, padX: 8, compactPadX: 9, narrow: { size: 11, padX: 6 } },
  /** Le prix sous le nom d'un article, la taille à sa droite. */
  price: { size: 16, line: 20, gap: 2 },
  /** La taille d'un article (« Taille M »), à droite du prix. */
  sizeLabel: { size: 11 },
  /** « Petit picto » : le caisson du picto et son glyphe (30 pour un dossier, 32 pour un article). */
  icon: { box: 30, glyph: 19, gap: 8 },
  articleIcon: { box: 32, glyph: 20 },
  /**
   * LE CAISSON CARRÉ — un grand picto en haut d'une tuile carrée, ou une photo « à gauche » du nom (et de la pastille d'un
   * dossier, du prix d'un article).
   */
  box: { size: 56, glyph: 36, gap: 8 },
  /**
   * UNE TUILE CARRÉE À PICTO (petit ou grand) : le caisson en haut, le nom et ce qu'elle contient (la pastille, le prix)
   * calés en bas — `gap` entre les deux blocs d'un dossier (`articleGap` d'un article), `nameGap` entre le nom et la
   * pastille (`largeNameGap` sous un grand picto).
   */
  square: { gap: 6, articleGap: 4, nameGap: 4, largeNameGap: 5 },
  /** Un dossier « texte seul » : le nom puis la pastille, centrés. */
  compact: { padY: 10, padX: 8, gap: 9, name: { size: 13.5, line: 17 } },
  /** Un article « texte seul » : le nom puis le prix (la taille dessous), centrés. */
  textOnly: { gap: 8, name: { size: 13, line: 16 }, price: { size: 16, line: 20 }, sizeGap: 1 },
  /** Le rayon du caisson d'un picto ou d'une photo à gauche. */
  iconRadius: 8,
  /** Le trait du picto d'une tuile et d'un bouton du rail — un peu plus appuyé que celui de l'interface (1,75). */
  glyphStroke: 1.8,
  /** L'étiquette du nom posée sur la photo d'un dossier : au papier du thème, le compte à droite du nom. */
  label: { inset: 8, padY: 5, padX: 8, radius: 8, gap: 8, countSize: 12 },
  /** Le texte sous le bandeau d'une vignette d'article : la marque (sa ligne et sa taille), le nom, le prix. */
  vignetteText: { top: 8, brand: 12, brandSize: 10, brandGap: 1, bottom: 6 },
  /** Le code-barres posé sur le bandeau d'une vignette — plus petit sur une vignette réduite. */
  code: { inset: 8, size: 10, padY: 2, padX: 8 },
  codeReduced: { inset: 6, size: 9, padY: 1, padX: 6 },
} as const;

/**
 * LES VOILES — ce qu'on pose sous un picto ou un code pour le détacher d'une teinte ou d'une photo : le PAPIER du thème, à
 * ces opacités.
 * ⚠️ LE PAPIER, JAMAIS UN BLANC : l'encre posée dessus bascule en sombre (un gris clair), un blanc fixe non — le code-barres
 *    et le picto devenaient illisibles en sombre. Le papier bascule avec elle. En clair, rien ne change : le papier y est blanc.
 */
export const QUICK_VEIL = {
  /** Le caisson d'un picto, et la pastille du compte, sur une tuile teintée. */
  icon: 0.7,
  /** Le caisson du picto d'un bouton OUVERT du rail — le voile du caisson d'une tuile (style « plein » de la maquette). */
  rail: 0.7,
  /** Le code-barres posé sur le bandeau d'une vignette, et l'étiquette du nom sur la photo d'un dossier. */
  code: 0.86,
} as const;

/** Un voile pour le web : le papier du thème à cette opacité, qui suit le mode clair ou sombre de la page. */
export function webPaperVeil(alpha: number): string {
  return `color-mix(in oklab, var(--paper) ${Math.round(alpha * 100)}%, transparent)`;
}

/**
 * Un voile pour React Native, qui ne connaît pas `color-mix` : le papier du thème (hex) à cette opacité.
 * ⚠️ UN PAPIER QUI N'EST PAS UN HEX LÈVE : rendre une couleur devinée dessinerait un voile faux, sans erreur.
 */
export function nativePaperVeil(paper: string, alpha: number): string {
  const m = /^#([0-9a-f]{6})$/i.exec(paper.trim());
  if (!m) throw new Error(`Voile de tuile : le papier « ${paper} » n'est pas une couleur hex.`);
  const n = parseInt(m[1]!, 16);
  return `rgba(${n >> 16},${(n >> 8) & 255},${n & 255},${alpha})`;
}

/**
 * LES VERSIONS D'UNE TUILE — ce qui décide de sa forme et de sa hauteur.
 * - ce qui ouvre un niveau : `compact` (texte seul : le nom et la pastille, centrés), `icon` (carré à petit picto),
 *   `largeIcon` (carré à grand picto), `photo` (la photo entière, le nom posé dessus), `photoReduced` (un bandeau de
 *   photo réduit, le nom et la pastille dessous), `photoLeft` (la photo dans le caisson à gauche) ;
 * - un article : `text` (texte seul : le nom et le prix, centrés), `articleIcon` et `articleLarge` (carrés à picto),
 *   `vignette` (bandeau 16:10), `vignetteReduced` (bandeau divisé par deux), `thumb` (la photo dans le caisson à gauche,
 *   la marque, le nom et le prix à côté) ;
 * - `empty` : une case vide.
 */
export type QuickTileVariant =
  | 'compact' | 'icon' | 'largeIcon' | 'photo' | 'photoReduced' | 'photoLeft'
  | 'text' | 'articleIcon' | 'articleLarge' | 'vignette' | 'vignetteReduced' | 'thumb'
  | 'empty';

/** La taille d'une photo de tuile — celle du contrat (`QUICK_PHOTO_SIZES`), que la charte ne lit pas. */
export type QuickPhotoSizeShape = 'normal' | 'reduced' | 'left';

/** Ce que la règle du contrat (`tileFace`) montre d'une tuile, réduit à ce qui décide de sa forme. */
export interface QuickFaceShape { kind: 'default' | 'none' | 'icon' | 'photo'; large?: boolean; size?: QuickPhotoSizeShape }

/**
 * LA VERSION D'UNE TUILE — ce que montre la règle du contrat, et la nature de la tuile.
 * ⚠️ ELLE SE DÉCIDE SUR CE QUI EXISTE, JAMAIS SUR CE QUI EST DÉJÀ TÉLÉCHARGÉ : l'appelant passe la règle du contrat
 *    appliquée aux ADRESSES. Décidée sur les fichiers, une tuile « Photo » passerait de picto à vignette à l'arrivée de
 *    son image, et toute sa rangée sauterait de hauteur.
 * ⚠️ UN ARTICLE SANS PHOTO GARDE SA FORME DE PHOTO (`default` à sa taille) : des hachures à la place de l'image, jamais
 *    une autre version — la grille ne change pas quand la photo arrive. Un dossier non habillé reste en texte seul.
 */
export function quickTileVariant(tile: { kind: string }, face: QuickFaceShape): QuickTileVariant {
  if (tile.kind === 'empty') return 'empty';
  const article = tile.kind === 'article';
  switch (face.kind) {
    case 'icon':
      if (article) return face.large ? 'articleLarge' : 'articleIcon';
      return face.large ? 'largeIcon' : 'icon';
    case 'none':
      return article ? 'text' : 'compact';
    case 'photo':
    case 'default':
      if (!article) {
        if (face.kind !== 'photo') return 'compact';
        return face.size === 'left' ? 'photoLeft' : face.size === 'reduced' ? 'photoReduced' : 'photo';
      }
      return face.size === 'left' ? 'thumb' : face.size === 'reduced' ? 'vignetteReduced' : 'vignette';
  }
}

/**
 * LA CLASSE DE TAILLE D'UNE VERSION — ce qui règle ses colonnes : texte seul, petit picto, grand picto, photo (toute
 * version qui porte une image, à gauche comprise). Une case vide compte pour du texte seul.
 */
export type QuickSizeClass = 'text' | 'icon' | 'large' | 'photo';

export function quickSizeClass(variant: QuickTileVariant): QuickSizeClass {
  switch (variant) {
    case 'compact': case 'text': case 'empty': return 'text';
    case 'icon': case 'articleIcon': return 'icon';
    case 'largeIcon': case 'articleLarge': return 'large';
    default: return 'photo';
  }
}

const CLASS_RANK: Record<QuickSizeClass, number> = { text: 0, icon: 1, large: 2, photo: 3 };

/**
 * LA CLASSE QUI RÈGLE LES COLONNES D'UN NIVEAU — la plus grande présente (photo › grand picto › petit picto › texte).
 * ⚠️ ELLE SE LIT SUR LES VERSIONS DEMANDÉES, pas sur celles qui tiennent (`quickFitVariant`) : la largeur dépend des
 *    colonnes, qui dépendent de cette classe — la lire après coup tournerait en rond.
 * ⚠️ Un niveau sans tuile garde la colonne de référence (`photo`).
 */
export function quickDrivingClass(variants: readonly QuickTileVariant[]): QuickSizeClass {
  let best: QuickSizeClass | null = null;
  for (const v of variants) {
    const k = quickSizeClass(v);
    if (best === null || CLASS_RANK[k] > CLASS_RANK[best]) best = k;
  }
  return best ?? 'photo';
}

/** Les versions dont la tuile est un CARRÉ (une case vide n'en décide pas : elle suit sa rangée). */
const SQUARE_VARIANTS: ReadonlySet<QuickTileVariant> = new Set(['compact', 'text', 'icon', 'largeIcon', 'articleIcon', 'articleLarge']);
export function isQuickSquare(variant: QuickTileVariant): boolean {
  return SQUARE_VARIANTS.has(variant);
}

/**
 * LE BANDEAU PHOTO DANS UNE COLONNE DE CETTE LARGEUR — au format 16:10, ou sa moitié pour une vignette réduite.
 * ⚠️ IL SUIT LA COLONNE, PAS LA TUILE : une tuile large de deux colonnes garde la hauteur de bandeau de ses voisines,
 *    sinon elle imposerait une rangée deux fois plus haute que son contenu.
 */
export function quickBandHeight(unitW: number, size: 'normal' | 'reduced'): number {
  const normal = Math.round((unitW * QUICK_TILE.band.height) / QUICK_TILE.band.width);
  return size === 'reduced' ? Math.round(normal / 2) : normal;
}

/** Le texte d'une vignette sous son bandeau : la marque, le nom (une ligne), le prix. */
function vignetteTextHeight(): number {
  const b = QUICK_TILE_BODY;
  const vt = b.vignetteText;
  return vt.top + vt.brand + vt.brandGap + b.nameLines * b.articleName.line + b.price.gap + b.price.line + vt.bottom;
}

/** La rangée du nom et de la pastille sous le bandeau réduit d'un dossier. */
function folderRowHeight(): number {
  const b = QUICK_TILE_BODY;
  return 2 * b.pad + Math.max(b.nameLines * b.name.line, b.pill.height);
}

/**
 * LE CÔTÉ D'UN CARRÉ de cette classe dans une colonne de cette largeur.
 * ⚠️ LA CLASSE QUI RÈGLE LES COLONNES REMPLIT SA COLONNE en vrai carré ; une classe plus petite garde SA cible — un petit
 *    picto voisin d'une photo n'enfle pas à sa taille. Jamais plus large que la colonne.
 */
function squareSide(cls: 'text' | 'icon' | 'large', unitW: number, driving: QuickSizeClass): number {
  const u = Math.round(unitW);
  return cls === driving ? u : Math.min(u, QUICK_TILE_SQUARE[cls].target);
}

/** Ce qu'une version demande de hauteur sur UNE rangée — jamais moins que la cible tactile. */
function oneRow(variant: QuickTileVariant, unitW: number, driving: QuickSizeClass): number {
  return Math.max(QUICK_TILE_BODY.touch, contentRow(variant, unitW, driving));
}

/**
 * La hauteur du CONTENU d'une version sur une rangée.
 * ⚠️ UN CARRÉ A LA HAUTEUR DE SON CÔTÉ ; ailleurs, une ligne porte ce qui y est le plus haut — le nom, la pastille ou le
 *    caisson posé à côté —, jamais l'un d'eux supposé égal aux autres.
 */
function contentRow(variant: QuickTileVariant, unitW: number, driving: QuickSizeClass): number {
  const b = QUICK_TILE_BODY;
  const frame = 2 * b.border;
  const inner = frame + 2 * b.pad;
  const name = b.nameLines * b.name.line;
  const articleName = b.nameLines * b.articleName.line;
  const price = b.price.gap + b.price.line;
  const brand = b.vignetteText.brand + b.vignetteText.brandGap;
  switch (variant) {
    case 'compact': case 'text': return squareSide('text', unitW, driving);
    case 'icon': case 'articleIcon': return squareSide('icon', unitW, driving);
    case 'largeIcon': case 'articleLarge': return squareSide('large', unitW, driving);
    case 'empty':
      return inner + Math.max(name, b.pill.height);
    case 'photoLeft':
      return inner + b.box.size;
    case 'thumb':
      return inner + Math.max(b.box.size, brand + articleName + price);
    case 'photo':
      return frame + quickBandHeight(unitW, 'normal');
    case 'photoReduced':
      return frame + quickBandHeight(unitW, 'reduced') + folderRowHeight();
    case 'vignette':
      return frame + quickBandHeight(unitW, 'normal') + vignetteTextHeight();
    case 'vignetteReduced':
      return frame + quickBandHeight(unitW, 'reduced') + vignetteTextHeight();
  }
}

/**
 * LA HAUTEUR PROPRE D'UNE TUILE — celle de son contenu, ou le côté de son carré.
 * ⚠️ UNE TUILE HAUTE (h = 2) VAUT DEUX TUILES ET UNE GOUTTIÈRE : seule dans ses rangées, elle garde sa proportion.
 * - `unitW` : la largeur d'une colonne, dont dépendent le bandeau photo et le côté d'un carré ;
 * - `driving` : la classe qui règle les colonnes du niveau (`quickGridColumns`) — `photo` par défaut.
 */
export function quickTileHeight(variant: QuickTileVariant, opts: { unitW: number; h: number; gap?: number; driving?: QuickSizeClass }): number {
  const h = Math.max(1, Math.floor(opts.h));
  return oneRow(variant, opts.unitW, opts.driving ?? 'photo') * h + (opts.gap ?? QUICK_TILE.gap) * (h - 1);
}

/** Les versions dont la photo GRANDIT avec la tuile — un bandeau, ou la photo entière d'un dossier. */
export type QuickBandVariant = 'photo' | 'photoReduced' | 'vignette' | 'vignetteReduced';

/**
 * LE BANDEAU D'UNE TUILE DE CETTE HAUTEUR — tout ce qui n'est ni son texte ni son filet. C'est lui qui grandit quand la
 * rangée est plus haute que la tuile.
 */
export function quickBandIn(variant: QuickBandVariant, tileHeight: number): number {
  const text = variant === 'photo' ? 0 : variant === 'photoReduced' ? folderRowHeight() : vignetteTextHeight();
  return tileHeight - 2 * QUICK_TILE_BODY.border - text;
}

/** La hauteur de la rangée du nom sous le bandeau réduit d'un dossier (`photoReduced`). */
export function quickFolderRowHeight(): number {
  return folderRowHeight();
}

/**
 * LE CADRE D'UNE PHOTO DE TUILE, À LA COLONNE DE RÉFÉRENCE — celui que la fenêtre « Cadrer la photo » du studio propose :
 * le bandeau qu'aura une tuile de cet encombrement, ou le caisson carré d'une photo à gauche.
 */
export function quickBandBox(variant: QuickBandVariant | 'photoLeft' | 'thumb', w: number, h: number): { width: number; height: number } {
  if (variant === 'photoLeft' || variant === 'thumb') return { width: QUICK_TILE_BODY.box.size, height: QUICK_TILE_BODY.box.size };
  const unitW = QUICK_TILE.width;
  const cols = Math.max(1, Math.floor(w));
  return {
    width: cols * unitW + (cols - 1) * QUICK_TILE.gap,
    height: quickBandIn(variant, quickTileHeight(variant, { unitW, h })),
  };
}

/** Une pastille de dossier telle qu'elle se dessine : son texte (le libellé entier ou court), sa police, sa marge. */
export interface QuickPillForm { short: boolean; size: number; padX: number }

/**
 * LA LARGEUR ESTIMÉE D'UN LIBELLÉ EN GRAS — pour une surface qui ne peut pas mesurer avant de dessiner (la caisse React
 * Native). Large à dessein : elle resserre la pastille un peu tôt plutôt que de la laisser déborder.
 */
export function quickTextWidthEstimate(label: string, size: number): number {
  return Math.ceil(label.length * size * 0.6);
}

/**
 * LA FORME DE LA PASTILLE DANS CETTE PLACE — le libellé entier (« 12 articles ») en 12 px ; s'il ne tient pas, resserré
 * en 11 px ; s'il ne tient toujours pas et qu'une forme courte existe (« 12 art. », le composeur du studio seulement),
 * celle-ci (maquette du studio, `txtW`).
 * - `room` : la largeur disponible pour la pastille.
 * - `measure` : la largeur du libellé en gras à cette taille — MESURÉE là où la surface le peut (le studio, au canevas,
 *   comme la maquette), estimée sinon.
 */
export function quickPillForm(
  full: string, short: string | null, room: number, padX: number = QUICK_TILE_BODY.pill.padX,
  measure: (label: string, size: number) => number = quickTextWidthEstimate,
): QuickPillForm {
  const p = QUICK_TILE_BODY.pill;
  const width = (label: string, size: number, pad: number): number => measure(label, size) + 2 * pad + 2;
  if (width(full, p.size, padX) <= room) return { short: false, size: p.size, padX };
  if (width(full, p.narrow.size, p.narrow.padX) <= room || short === null) return { short: false, size: p.narrow.size, padX: p.narrow.padX };
  return { short: true, size: p.narrow.size, padX: p.narrow.padX };
}

/**
 * LE RAIL DU « MENU FIXE » — ses mesures, relevées sur la maquette du studio (`eRail` au composeur, `pRail` à l'aperçu).
 * ⚠️ LE STUDIO ET LA CAISSE LES LISENT ICI : l'aperçu doit montrer le rail que le caissier touchera.
 * ⚠️ UN BOUTON SANS VISUEL GARDE LA HAUTEUR DES AUTRES (`buttonMinHeight` = visuel + deux marges) : une tuile non
 *    habillée n'a pas de picto, et réduite à son nom elle tomberait sous la taille d'un appui au doigt.
 */
export const QUICK_RAIL = {
  width: 150,
  /** Marges intérieures du rail : 12 en haut et en bas, 8 sur les côtés ; 5 entre deux boutons. */
  padY: 12, padX: 8, gap: 5,
  /** Un bouton : sa marge, son visuel (picto ou photo) et le glyphe du picto, son nom. */
  buttonPad: 8, visual: 26, glyph: 13, font: 12, lineHeight: 1.25,
  buttonMinHeight: 26 + 2 * 8,
  /** La hauteur au-delà de laquelle le rail défile au composeur du studio (maquette : 560). */
  composerMaxHeight: 560,
} as const;

/**
 * LE MENU FIXE DU TÉLÉPHONE (« pocket ») — réduit à ses pictos : une colonne de 56, des boutons de 44 au visuel de 26, le
 * nom dans l'infobulle et le libellé d'accessibilité (maquette du studio, `pRailPocket`). Sur un écran de 393, le rail
 * entier ne laisserait aux tuiles qu'une colonne.
 * ⚠️ LE STUDIO ET LA CAISSE LES LISENT ICI, comme `QUICK_RAIL` : l'aperçu Pocket montre le rail que le téléphone dessine.
 */
export const QUICK_RAIL_POCKET = { width: 56, button: 44, visual: 26, glyph: 13, padY: 10, gap: 5, initials: 11 } as const;

/**
 * LES INITIALES D'UN BOUTON SANS PICTO NI PHOTO — les deux premiers mots, une lettre chacun ; « · » faute de lettre
 * (maquette `mono`). Sans elles, un bouton « Texte seul » du menu réduit serait une case vide.
 */
export function quickRailInitials(name: string): string {
  const words = name.replace(/[^\p{L}\p{N}]+/gu, ' ').split(' ').filter(Boolean);
  return words.slice(0, 2).map((w) => [...w][0]!).join('').toUpperCase() || '·';
}

/**
 * LES COULEURS D'UN BOUTON DU RAIL — la règle de la maquette : OUVERT, le bouton prend la teinte de sa tuile et le
 * caisson de son picto le voile (`QUICK_VEIL.rail`) ; AU REPOS, le bouton est sur papier et le caisson porte la teinte.
 * ⚠️ SANS TEINTE (`tone` nul), la tuile garde les couleurs d'avant l'habillage : la sélection du studio, le neutre.
 * ⚠️ UNE FONCTION ET NON DEUX RENDUS : le studio la lit en variables CSS, la caisse en couleurs de son thème — écrite
 *    deux fois, l'aperçu montrerait un autre rail que la caisse. Chacun lui passe son voile (`webPaperVeil`,
 *    `nativePaperVeil`) : seul le moyen de l'écrire diffère.
 */
export function quickRailLook(tone: { bg: string; fg: string } | null, open: boolean, base: {
  paper: string; selected: string; neutral: string; neutralFg: string; veil: string;
}): { background: string; iconBox: string; iconColor: string } {
  return {
    background: open ? tone?.bg ?? base.selected : base.paper,
    iconBox: open ? base.veil : tone?.bg ?? base.neutral,
    iconColor: tone?.fg ?? base.neutralFg,
  };
}

/**
 * LES TEINTES D'UNE TUILE — pour chaque clé que le studio propose, les jetons de la charte qu'elle emploie : le fond, le
 * texte et le picto, le filet, la pastille (maquette du studio, `PAL`).
 * ⚠️ DES NOMS DE JETONS, JAMAIS DES COULEURS : le web les lit en variables CSS (`var(--amber-bg)`), la caisse dans son
 *    thème (`teal-*`, `ink-*`) ou dans `nativeAccents` — la tuile suit donc le mode clair ou sombre de chacun.
 * ⚠️ LE FOND ET LE TEXTE SONT DES PAIRES QUI BASCULENT EN SOMBRE (`-bg`/`-fg`), jamais deux crans d'une rampe : la rampe
 *    ne bascule pas, l'encre du nom posé dessus si — le nom d'une tuile teal sortait ainsi à 1,05:1. Le filet et la
 *    pastille, eux, se lisent sur le papier : ils peuvent rester des crans de rampe.
 * ⚠️ LES CLÉS DOIVENT ÊTRE CELLES DU CONTRAT (`QUICK_TILE_COLORS`, `@orgazone/sync-contract`) : la charte ne dépend pas
 *    du contrat, un banc du back-office confronte les deux listes.
 */
export const QUICK_TILE_PALETTE = {
  teal: { bg: 'teal-bg', fg: 'teal-fg', bd: 'teal-400', dot: 'teal-500' },
  amber: { bg: 'amber-bg', fg: 'amber-fg', bd: 'amber', dot: 'amber' },
  rose: { bg: 'rose-bg', fg: 'rose-fg', bd: 'rose', dot: 'rose' },
  violet: { bg: 'violet-bg', fg: 'violet-fg', bd: 'violet', dot: 'violet' },
  blue: { bg: 'blue-bg', fg: 'blue-fg', bd: 'blue', dot: 'blue' },
  green: { bg: 'green-bg', fg: 'green-fg', bd: 'green', dot: 'green' },
  orange: { bg: 'orange-bg', fg: 'orange-fg', bd: 'orange', dot: 'orange' },
  ink: { bg: 'ink-100', fg: 'ink-700', bd: 'ink-300', dot: 'ink-500' },
} as const;
export type QuickTilePaletteKey = keyof typeof QUICK_TILE_PALETTE;

/** Les échelles d'un thème natif où se lisent les jetons d'une teinte. */
export interface NativeTileScales {
  teal: Record<string | number, string>;
  ink: Record<string | number, string>;
  /** `nativeAccents(mode)` — les accents sémantiques du mode, dont les paires `-bg`/`-fg` qui basculent en sombre. */
  accents: Record<string, string>;
}

/**
 * UNE TEINTE DE TUILE EN COULEURS NATIVES — la même table que le web, lue dans les échelles du thème de l'app.
 * ⚠️ UN JETON INTROUVABLE LÈVE, il ne rend pas `undefined` : React Native dessinerait une tuile sans fond ni filet,
 *    sans erreur — exactement le défaut que la table existe pour empêcher.
 */
export function nativeTileTone(key: QuickTilePaletteKey, scales: NativeTileScales): { bg: string; fg: string; bd: string; dot: string } {
  const read = (token: string): string => {
    const ramp = /^(teal|ink)-(\d+)$/.exec(token);
    const v = ramp ? scales[ramp[1] as 'teal' | 'ink'][ramp[2]!] : scales.accents[token];
    if (!v) throw new Error(`Teinte de tuile « ${key} » : jeton « ${token} » absent du thème.`);
    return v;
  };
  const p = QUICK_TILE_PALETTE[key];
  return { bg: read(p.bg), fg: read(p.fg), bd: read(p.bd), dot: read(p.dot) };
}

/**
 * LES COLONNES QUAND LA DISPOSITION LAISSE LA CAISSE CHOISIR — autant de tuiles de référence que la largeur en tient.
 * ⚠️ C'est la règle de `repeat(auto-fill, minmax(<largeur>, 1fr))` d'une grille du navigateur.
 */
export function autoColumns(availableWidth: number, tileWidth: number = QUICK_TILE.width, gap: number = QUICK_TILE.gap): number {
  return Math.max(1, Math.floor((availableWidth + gap) / (tileWidth + gap)));
}

/**
 * Les colonnes d'une grille de cette largeur, la largeur d'une case, la classe qui les a réglées — et `capped` quand les
 * cases sont plafonnées (la place libre reste alors à droite de la grille).
 */
export interface QuickGridColumns { cols: number; unitW: number; gap: number; driving: QuickSizeClass; capped: boolean }

/**
 * LES COLONNES D'UNE GRILLE DE TUILES — réglées par la plus grande classe du niveau (`quickDrivingClass`).
 * - COLONNES IMPOSÉES (ou un niveau qui porte une photo) : au plus le nombre réglé, jamais sous la colonne minimale de la
 *   classe (104 texte seul, 106 petit picto, 150 sinon) ; les cases s'étirent pour remplir la rangée, comme `1fr`.
 * - COLONNES AUTOMATIQUES d'un niveau de carrés : le nombre qui approche le mieux la CIBLE de la classe sans passer sous
 *   son minimum ; si la case dépasse encore le maximum, une colonne de plus ; et au-delà, la case est PLAFONNÉE.
 * ⚠️ 🔴 LE NOMBRE RÉGLÉ EST UN MAXIMUM, JAMAIS UNE CONTRAINTE : « 5 colonnes » sur un petit écran donnait des cases de
 *    104 px pour des photos. Sur un grand écran, où les cinq tiennent, rien ne change.
 */
export function quickGridColumns(width: number, cols: number | null, driving: QuickSizeClass = 'photo'): QuickGridColumns {
  const gap = QUICK_TILE.gap;
  if (cols !== null || driving === 'photo') {
    const minW = driving === 'text' || driving === 'icon' ? QUICK_TILE_SQUARE.imposed[driving] : QUICK_TILE.width;
    const fit = autoColumns(width, minW, gap);
    const n = Math.max(1, Math.min(Math.floor(cols ?? fit), fit));
    return { cols: n, unitW: Math.max(1, (width - gap * (n - 1)) / n), gap, driving, capped: false };
  }
  const s = QUICK_TILE_SQUARE[driving];
  const most = autoColumns(width, s.min, gap);
  let n = Math.max(1, Math.min(most, Math.round((width + gap) / (s.target + gap))));
  let unitW = (width - gap * (n - 1)) / n;
  if (unitW > s.max && n < most) { n += 1; unitW = (width - gap * (n - 1)) / n; }
  const capped = unitW > s.max;
  return { cols: n, unitW: capped ? s.max : Math.max(1, unitW), gap, driving, capped };
}

/**
 * LA PLACE QUE CETTE VERSION LAISSE AU NOM, dans une tuile de cette largeur — ce qui reste une fois posé le caisson d'une
 * photo « à gauche ». Partout ailleurs le nom a toute la largeur de la tuile (le picto d'un carré est au-dessus).
 */
export function quickNameRoom(variant: QuickTileVariant, tileWidth: number): number {
  const b = QUICK_TILE_BODY;
  const inner = tileWidth - 2 * (b.pad + b.border);
  return variant === 'photoLeft' || variant === 'thumb' ? inner - b.box.size - b.box.gap : inner;
}

/** Ce que devient une version dont le visuel ne laisse plus la place au nom — la photo, elle, est gardée. */
const NARROW: Partial<Record<QuickTileVariant, QuickTileVariant>> = {
  icon: 'compact', largeIcon: 'compact', photoLeft: 'photo',
  articleIcon: 'text', articleLarge: 'text', thumb: 'vignetteReduced',
};

/**
 * LA VERSION QUI TIENT DANS CETTE LARGEUR DE TUILE — celle demandée, sauf quand son visuel posé à côté du nom ne lui
 * laisse plus `nameMin` : le visuel s'efface alors (un dossier devient compact, un article texte seul ; une photo à
 * gauche devient la photo entière ou la vignette réduite).
 * ⚠️ CE N'EST PAS UN RÉGLAGE : l'habillage enregistré ne change pas, et le visuel revient dès que la tuile est assez
 *    large. La caisse et le studio l'appliquent à la même largeur — la hauteur ET le rendu, sinon la rangée et la tuile
 *    se contrediraient.
 */
export function quickFitVariant(variant: QuickTileVariant, tileWidth: number): QuickTileVariant {
  const narrow = NARROW[variant];
  return narrow && quickNameRoom(variant, tileWidth) < QUICK_TILE_BODY.nameMin ? narrow : variant;
}

/**
 * LE NOMBRE DE LIGNES DU NOM D'UNE TUILE DE CETTE HAUTEUR — la dernière se coupe par « … » (maquette `nL`).
 * - un petit carré à picto : deux si sa hauteur intérieure atteint `twoLinesFrom` (dossier ou article), une sinon ;
 * - un grand picto et une photo à gauche d'un dossier : deux, toujours (le nom a la hauteur du caisson de 56) ;
 * - un grand picto d'article : la règle du petit carré ;
 * - un texte seul (dossier ou article) : trois ;
 * - le reste (photo entière, photo réduite, vignettes, photo à gauche d'un article) : une.
 * ⚠️ LE STUDIO ET LA CAISSE LA LISENT TOUS DEUX : écrite deux fois, l'aperçu montrerait un nom que la caisse coupe.
 */
export function quickNameLines(variant: QuickTileVariant, tileHeight: number): number {
  const b = QUICK_TILE_BODY;
  const inner = tileHeight - 2 * (b.pad + b.border);
  switch (variant) {
    case 'icon': return inner >= b.twoLinesFrom.folder ? 2 : 1;
    case 'articleIcon': case 'articleLarge': return inner >= b.twoLinesFrom.article ? 2 : 1;
    case 'largeIcon': case 'photoLeft': return 2;
    case 'compact': case 'text': return b.textLines;
    default: return b.nameLines;
  }
}

/** La largeur d'une tuile de `w` colonnes : ses cases et les gouttières qu'elle enjambe. */
export function quickTileWidth(w: number, unitW: number, gap: number = QUICK_TILE.gap): number {
  const n = Math.max(1, Math.floor(w));
  return n * unitW + (n - 1) * gap;
}

/** Une place dans la grille : colonne et rangée de départ (depuis 0), encombrement. */
export interface GridSlot { col: number; row: number; w: number; h: number }

/**
 * LA PLACE DE CHAQUE TUILE — le placement automatique de la grille CSS, « creux permis ».
 * Un curseur avance rangée par rangée ; chaque tuile prend la PREMIÈRE place libre à partir du curseur, et le curseur
 * ne revient jamais en arrière : une case laissée vide par une tuile double qui ne tenait pas en fin de rangée le reste.
 * ⚠️ Une tuile plus large que la grille est ramenée à sa largeur — sur un écran étroit en colonnes automatiques.
 */
export function packGrid(items: readonly { w: number; h: number }[], cols: number): { slots: GridSlot[]; rows: number } {
  const width = Math.max(1, Math.floor(cols));
  const taken: boolean[][] = [];
  const isFree = (row: number, col: number, w: number, h: number): boolean => {
    if (col + w > width) return false;
    for (let r = row; r < row + h; r += 1) {
      for (let c = col; c < col + w; c += 1) if (taken[r]?.[c]) return false;
    }
    return true;
  };
  const take = (row: number, col: number, w: number, h: number): void => {
    for (let r = row; r < row + h; r += 1) {
      taken[r] = taken[r] ?? [];
      for (let c = col; c < col + w; c += 1) taken[r]![c] = true;
    }
  };
  let cursorRow = 0;
  let cursorCol = 0;
  let rows = 0;
  const slots = items.map((item) => {
    const w = Math.min(Math.max(1, item.w), width);
    const h = Math.max(1, item.h);
    let row = cursorRow;
    let col = cursorCol;
    while (!isFree(row, col, w, h)) {
      col += 1;
      if (col + w > width) { row += 1; col = 0; }
    }
    take(row, col, w, h);
    cursorRow = row;
    cursorCol = col + w;
    rows = Math.max(rows, row + h);
    return { col, row, w, h };
  });
  return { slots, rows };
}

/**
 * Une tuile à placer : son encombrement en cases, sa hauteur propre (`quickTileHeight`), et ce qui décide d'une rangée
 * compacte — `square` (sa version est un carré, `isQuickSquare`) et `empty` (une case vide suit sa rangée sans la décider,
 * ou la rangée réelle la plus proche quand elle est seule à la ligne).
 */
export interface QuickGridItem { w: number; h: number; height: number; square?: boolean; empty?: boolean }

/** Le rectangle d'une tuile dans sa grille, en pixels. */
export interface QuickGridRect { x: number; y: number; width: number; height: number }

/**
 * LA GRILLE D'UN NIVEAU — chaque tuile à sa place (`packGrid`), chaque rangée à la hauteur de sa tuile la plus haute, et
 * chaque tuile étirée à la hauteur de ses rangées : une rangée ne mélange jamais deux tailles.
 * ⚠️ UNE TUILE HAUTE NE TASSE PAS SES RANGÉES : si elles ne lui suffisent pas, le manque se partage entre elles à parts
 *    égales (le reste de l'arrondi aux premières).
 * ⚠️ UNE RANGÉE COMPACTE GARDE SES CARRÉS : une rangée faite UNIQUEMENT de carrés 1×1 plus bas que la colonne (des petits
 *    pictos dans un niveau réglé par une photo) les garde carrés — côté = hauteur de la rangée, calés à gauche de leur
 *    case (`narrow`). Une photo, une tuile sur deux cases, ou une rangée aussi haute que la colonne : chaque tuile prend
 *    sa case entière.
 * ⚠️ UNE CASE VIDE NE DÉCIDE D'AUCUNE RANGÉE dès que le niveau porte une tuile réelle : une rangée qu'aucune tuile
 *    réelle n'occupe (une case vide passée seule à la ligne) prend la hauteur ET la forme carrée de la rangée réelle la
 *    plus proche, au-dessus d'abord — comme la case « + Ajouter » du studio. Livrée à sa hauteur propre (48 px) sur
 *    toute sa case, elle se lirait comme un bandeau de texte seul au milieu de petits pictos. Une case vide haute ne fait
 *    pas grandir les rangées qu'elle traverse.
 * ⚠️ LA CAISSE ET LE STUDIO LA LISENT TOUS DEUX : le studio ne laisse pas son navigateur dimensionner les rangées — celui-ci
 *    répartit autrement le manque d'une tuile haute, et l'aperçu montrerait un autre écran que la caisse.
 */
export function quickGridLayout(items: readonly QuickGridItem[], cols: number, unitW: number, gap: number = QUICK_TILE.gap): {
  /** La place de chaque tuile en cases — ce qu'une grille CSS reçoit (`gridColumn`, `gridRow`). */
  slots: GridSlot[];
  rects: QuickGridRect[]; rows: number[]; height: number;
  /** Les tuiles réduites au carré de leur rangée compacte (leur rectangle a déjà la largeur du carré). */
  narrow: boolean[];
} {
  const { slots, rows: count } = packGrid(items, cols);
  const rows = new Array<number>(count).fill(0);
  // Dans un niveau fait seulement de cases vides, rien d'autre ne donne leur hauteur aux rangées : une case vide haute y
  // garde la sienne.
  const hasReal = items.some((item) => !item.empty);
  slots.forEach((s, i) => { if (s.h === 1) rows[s.row] = Math.max(rows[s.row]!, items[i]!.height); });
  const span = (from: number, h: number): number => rows.slice(from, from + h).reduce((a, b) => a + b, 0) + gap * (h - 1);
  slots.forEach((s, i) => {
    if (s.h === 1 || (hasReal && items[i]!.empty)) return;
    const lack = items[i]!.height - span(s.row, s.h);
    if (lack <= 0) return;
    const each = Math.floor(lack / s.h);
    let rest = lack - each * s.h;
    for (let r = s.row; r < s.row + s.h; r += 1) {
      rows[r] = rows[r]! + each + (rest > 0 ? 1 : 0);
      if (rest > 0) rest -= 1;
    }
  });
  // La rangée réelle que suit chaque rangée de cases vides (au-dessus d'abord, sinon au-dessous) ; `null` = elle se décide.
  const occupied = new Array<boolean>(count).fill(false);
  slots.forEach((s, i) => {
    if (items[i]!.empty) return;
    for (let r = s.row; r < s.row + s.h; r += 1) occupied[r] = true;
  });
  const follows = occupied.map((own, r) => {
    if (own) return null;
    for (let k = r - 1; k >= 0; k -= 1) if (occupied[k]) return k;
    for (let k = r + 1; k < count; k += 1) if (occupied[k]) return k;
    return null;
  });
  follows.forEach((src, r) => { if (src !== null) rows[r] = rows[src]!; });
  const ownCompact = rows.map((height, r) => {
    if (height >= unitW - 0.5) return false;
    let real = 0;
    for (let i = 0; i < slots.length; i += 1) {
      const s = slots[i]!;
      if (r < s.row || r >= s.row + s.h || items[i]!.empty) continue;
      real += 1;
      if (s.w !== 1 || s.h !== 1 || !items[i]!.square) return false;
    }
    return real > 0;
  });
  const compact = ownCompact.map((own, r) => (follows[r] !== null ? ownCompact[follows[r]!]! : own));
  const narrow = slots.map((s) => s.w === 1 && s.h === 1 && compact[s.row] === true);
  const top = (row: number): number => rows.slice(0, row).reduce((a, b) => a + b, 0) + gap * row;
  const rects = slots.map((s, i) => ({
    x: s.col * (unitW + gap),
    y: top(s.row),
    width: narrow[i] ? rows[s.row]! : s.w * unitW + (s.w - 1) * gap,
    height: span(s.row, s.h),
  }));
  return { slots, rects, rows, height: count === 0 ? 0 : top(count) - gap, narrow };
}
